import React, { useState, useEffect, useCallback, useRef } from "react";
import { Row, Col } from "react-bootstrap";
import { Spinner } from "../../Common";
import Button from "../../Common/Button";
import InputField from "../../Common/V2/Widgets/Input/index";
import DropDown from "../../Common/DropDown";
import styles from "./LientransferfLow.module.css";
import singleBackArrow from "../../public/images/singleBackArrow.svg";
import {
  WalletTransfer,
  TransferFee,
  ExecuteTransfer,
  LienBeneficiary,
  LienBeneficiaryDetails,
  lienBeneCountryList,
  AddNewBeneficiary,
} from "../../api";
import {
  buildMethodsMap,
  getFirstValidRoutingType,
  formatDisplayName,
  capitaliseFirst,
  trimFormData,
  SKIP_DYNAMIC_RENDER,
  ADDRESS_FIELD_NAMES,
  STEP_MAIN,
  STEP_ADDRESS,
  INITIAL_FORM,
} from "../../utils/utils";
import {reFormatDate} from "./../../Constants/helper"
import { validateMainStep, validateAddressStep } from "./BeneficiaryValidation";
import {
  AudFlag,
  Bank,
  BGPFlag,
  CadFlag,
  Calender,
  Dollar,
  EuroFlag,
  IndiaFlag,
  Refresh,
  SGDFlag,
  SuccessVideo,
  greenTick,
  USFlag,
  wallet,
  info,
  PaperAirplaneIcon,
} from "../../public/images/icon";

const TRANSFER_STEPS = { DETAILS: 1, SUMMARY: 2, CONFIRMATION: 3 };

 const PURPOSE_OPTIONS = [
    { value: "investment_capital", label: "Investment capital" },
    { value: "transfer_to_own_account", label: "Transfer to own account" },
    { value: "education_training", label: "Education and training" },
    { value: "loan_credit_repayment", label: "Loan repayment" },
  ];

const METHOD_DISPLAY = {
  LOCAL: {
    title: "Borderless Instant",
    duration: "0–1 Business Days",
    charges: "5 USD",
    recommended: true,
  },
  SWIFT: {
    title: "SWIFT",
    duration: "1–3 Business Days",
    charges: "15 USD",
    recommended: false,
  },
};

const CURRENCY_FLAG_MAP = {
  USD: USFlag,
  GBP: BGPFlag,
  EUR: EuroFlag,
  SGD: SGDFlag,
  CAD: CadFlag,
  AUD: AudFlag,
  INR: IndiaFlag,
};

const CurrencyFlag = ({ code, size = 20 }) => {
  const FlagSrc = CURRENCY_FLAG_MAP[code];
  if (!FlagSrc) {
    return (
      <img
        src={wallet}
        alt={code}
        width={size}
        height={size}
        style={{ objectFit: "contain" }}
      />
    );
  }
  return (
    <img
      src={FlagSrc}
      alt={code}
      width={size}
      height={size}
      style={{ objectFit: "contain" }}
    />
  );
};

const POLL_DELAYS = [3000, 6000, 10000];

const pollForStatus = async (userId, newBeneficiaryId) => {
  const wait = (ms) => new Promise((r) => setTimeout(r, ms));
  for (let i = 0; i < POLL_DELAYS.length; i++) {
    await wait(POLL_DELAYS[i]);
    try {
      const statusRes = await LienBeneficiary(userId, 1, 50, "");
      const list = statusRes?.data?.data?.beneficiaries || [];
      const matched = list.find((b) => b.beneficiaryId === newBeneficiaryId);
      if (matched?.status === "APPROVED" || matched?.status === "REJECTED") {
        return matched;
      }
    } catch {}
  }
  return null;
};

const useDebounce = (fn, delay) => {
  const timerRef = useRef(null);
  return useCallback(
    (...args) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => fn(...args), delay);
    },
    [fn, delay],
  );
};

const BeneficiaryCard = ({ beneficiary }) => (
  <div className={styles.beneCard}>
    <div className={styles.beneCardInfo}>
      <p className={styles.beneCardName}>
        {beneficiary?.nameOfBeni || beneficiary?.account_name || "Beneficiary"}
      </p>
      <p className={styles.beneCardSub}>
        {beneficiary?.maskAccountNo || beneficiary?.iban || ""}
        {beneficiary?.bankName && (
          <span className={styles.beneCardBank}> · {beneficiary.bankName}</span>
        )}
      </p>
    </div>
  </div>
);

const InfoTooltip = ({ text }) => {
  const [show, setShow] = useState(false);
  return (
    <span
      className={styles.tooltip}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
      style={{ display: "inline-flex", alignItems: "center", marginLeft: 4 }}
    >
      <img
        src={info}
        alt="info"
        width={14}
        height={14}
        style={{ cursor: "pointer" }}
      />
      {show && <span className={styles.tooltipBox}>{text}</span>}
    </span>
  );
};

const MethodCard = ({ methodKey, meta, isSelected, onSelect, showDetails }) => (
  <div
    className={`${styles.methodCard} ${isSelected ? styles.methodCardSelected : ""}`}
    onClick={() => onSelect(methodKey)}
  >
    <div className={styles.methodCardLeft}>
      <p className={styles.methodCardTitle}>{meta?.title || methodKey}</p>
      <p className={styles.methodCardDuration}>{meta?.duration || ""}</p>
      {showDetails && meta?.charges && (
        <p className={styles.methodCardCharges}>Fee: {meta.charges}</p>
      )}
    </div>
    <div className={styles.methodCardRight}>
      {meta?.recommended && (
        <span className={styles.methodCardBadge}>Recommended</span>
      )}
      {showDetails && (
      <span className={styles.methodCardChangeCta}>Select</span>
      )}
      {isSelected && !showDetails && (
        <span className={styles.methodCardChangeCta}>Change</span>
      )}
    </div>
  </div>
);
const ChangeMethodPanel = ({
  beneficiary,
  userId,
  existingBeneficiaries,
  onMethodChanged,
  onCancel,
}) => {
  const [availableMethods, setAvailableMethods] = useState({});
  const [loadingMethods, setLoadingMethods] = useState(true);
  const [selectedMethod, setSelectedMethod] = useState("");
  const [step, setStep] = useState(STEP_MAIN);
  const [formData, setFormData] = useState({ ...INITIAL_FORM });
  const [rawFields, setRawFields] = useState([]);
  const [accountFields, setAccountFields] = useState([]);
  const [addressFields, setAddressFields] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [pollingMsg, setPollingMsg] = useState("");
  const [fetchedDetails, setFetchedDetails] = useState(null);

  const rawFieldsRef = useRef([]);
  const formTopRef = useRef(null);

  useEffect(() => {
    const fetchMethods = async () => {
      if (!beneficiary?.account_currency && !beneficiary?.currency) return;
      setLoadingMethods(true);
      try {
        const currency = beneficiary.account_currency || beneficiary.currency;
        const country = beneficiary.country || beneficiary.country_code;
        const res = await lienBeneCountryList({}, currency, country, userId);
        const methodsFromApi = res?.data?.data?.[0]?.methods || [];
        const built = buildMethodsMap(methodsFromApi);
        setAvailableMethods(built);
      } catch (err) {
        console.error("Error fetching methods:", err);
      } finally {
        setLoadingMethods(false);
      }
    };
    fetchMethods();
  }, [beneficiary, userId]);

  const mapApiDetailsToForm = (details) => {
    if (!details) return {};
    return {
      first_name: details.firstName || details.first_name || "",
      last_name: details.lastName || details.last_name || "",
      entity_type: details.entityType || details.entity_type || "INDIVIDUAL",
      account_number: details.accountNo || details.account_number || "",
      swift_code: details.swiftCode || details.swift_code || "",
      iban: details.iban || "",
      account_routing_value1:
        details.accountRoutingValue || details.account_routing_value1 || "",
      account_routing_type1:
        details.accountRoutingType || details.account_routing_type1 || "",
      account_routing_value2:
        details.accountRoutingValue2 || details.account_routing_value2 || "",
      account_routing_type2:
        details.accountRoutingType2 || details.account_routing_type2 || "",
      country_code: details.country || details.country_code || "",
      country_name: details.country_name || details.country || "",
      account_currency: details.account_currency || details.currency || "",
      bank_country_code: details.country || details.bank_country_code || "",
      street_address: details.streetAddress || details.street_address || "",
      city: details.city || "",
      state: details.state || "",
      postcode: details.postcode || details.zip_code || "",
    };
  };

  useEffect(() => {
    if (!selectedMethod || !availableMethods[selectedMethod]) return;

    const fields = availableMethods[selectedMethod]?.fields || [];
    const formatted = fields.map((f) => ({
      ...f,
      displayName: formatDisplayName(f.displayName || f.fieldName),
    }));
    const accFields = formatted.filter(
      (f) =>
        f.required &&
        !SKIP_DYNAMIC_RENDER.has(f.fieldName) &&
        !ADDRESS_FIELD_NAMES.has(f.fieldName),
    );
    const addrFields = formatted.filter(
      (f) =>
        f.required &&
        !SKIP_DYNAMIC_RENDER.has(f.fieldName) &&
        ADDRESS_FIELD_NAMES.has(f.fieldName),
    );

    setAccountFields(accFields);
    setAddressFields(addrFields);
    setRawFields(fields);
    rawFieldsRef.current = fields;

    const mapped = mapApiDetailsToForm(fetchedDetails);

    const initVals = formatted.reduce((acc, f) => {
      if (SKIP_DYNAMIC_RENDER.has(f.fieldName)) return acc;
      if (f.fieldName === "account_routing_type1") {
        acc[f.fieldName] = getFirstValidRoutingType(
          formatted,
          "account_routing_type1",
        );
      } else if (f.fieldName === "account_routing_type2") {
        acc[f.fieldName] = getFirstValidRoutingType(
          formatted,
          "account_routing_type2",
        );
      } else {
        acc[f.fieldName] = mapped[f.fieldName] ?? "";
      }
      return acc;
    }, {});

    const newFormData = {
      ...INITIAL_FORM,
      ...initVals,
      country_code:
        mapped.country_code ||
        beneficiary.country ||
        beneficiary.country_code ||
        "",
      country_name:
        mapped.country_name ||
        beneficiary.country_name ||
        beneficiary.country ||
        "",
      account_currency:
        mapped.account_currency ||
        beneficiary.account_currency ||
        beneficiary.currency ||
        "",
      bank_country_code:
        mapped.bank_country_code ||
        beneficiary.country ||
        beneficiary.country_code ||
        "",
      first_name:
        mapped.first_name ||
        beneficiary.first_name ||
        beneficiary.nameOfBeni ||
        "",
      last_name: mapped.last_name || beneficiary.last_name || "",
      entity_type:
        mapped.entity_type || beneficiary.entity_type || "INDIVIDUAL",
      payment_method: selectedMethod,
      transfer_method: selectedMethod,
    };

    setFormData(newFormData);
    setErrors({});
  }, [selectedMethod, availableMethods, fetchedDetails]);

  const handleSelectMethod = async (methodKey) => {
  const currentMethod =
    beneficiary?.transfer_method || beneficiary?.method;

  if (methodKey === currentMethod) {
    onMethodChanged(beneficiary, methodKey);
    return;
  }

  try {
    const listRes = await LienBeneficiary(userId, 1, 100, "APPROVED");
    const freshList = listRes?.data?.data?.beneficiaries || [];

  const existing = freshList.find((b) => {
    const sameName =
      b.nameOfBeni?.trim().toLowerCase() ===
      beneficiary?.nameOfBeni?.trim().toLowerCase();

    const sameAccount =
      (b.account_number &&
        beneficiary?.account_number &&
        b.account_number === beneficiary.account_number) ||
      (b.iban &&
        beneficiary?.iban &&
        b.iban === beneficiary.iban) ||
      (b.maskAccountNo &&
        beneficiary?.maskAccountNo &&
        b.maskAccountNo === beneficiary.maskAccountNo);

    const sameCountry =
      b.country ===
      (beneficiary?.country || beneficiary?.country_code);

    const sameMethod =
      (b.transfer_method || b.method) === methodKey;

    return sameName && sameAccount && sameCountry && sameMethod;
  });

    if (existing) {
      onMethodChanged(existing, methodKey);
      return;
    }

  } catch (err) {
    }
    if (beneficiary?.beneficiaryId) {
      try {
        const detailsRes = await LienBeneficiaryDetails(
          beneficiary.beneficiaryId
        );
        setFetchedDetails(detailsRes?.data?.data || null);
      } catch (err) {
        console.error("Error fetching details:", err);
        setFetchedDetails(null);
      }
    }

    setSelectedMethod(methodKey);
    setStep(STEP_MAIN);
  };

  const clearError = (field) => setErrors((prev) => ({ ...prev, [field]: "" }));

  const handleChange = (field, value) => {
    clearError(field);
    if (field === "account_routing_value1") {
      setFormData((prev) => ({
        ...prev,
        account_routing_value1: (value || "").toUpperCase(),
        account_routing_type1: getFirstValidRoutingType(
          rawFieldsRef.current,
          "account_routing_type1",
        ),
      }));
    } else if (field === "account_routing_value2") {
      setFormData((prev) => ({
        ...prev,
        account_routing_value2: (value || "").toUpperCase(),
        account_routing_type2: getFirstValidRoutingType(
          rawFieldsRef.current,
          "account_routing_type2",
        ),
      }));
    } else if (field === "swift_code") {
      setFormData((prev) => ({
        ...prev,
        [field]: (value || "").toUpperCase(),
      }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: capitaliseFirst(value) }));
    }
  };

  const handleSubmit = async () => {
    const trimmed = trimFormData(formData);

    const { errors: stepErrors, isValid } = validateMainStep(
      trimmed,
      accountFields,
    );
    if (!isValid) {
      setErrors(stepErrors);
      formTopRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      return;
    }

    if (addressFields.length > 0) {
      const mapped = mapApiDetailsToForm(fetchedDetails);
      const addressValues = addressFields.reduce((acc, f) => {
        acc[f.fieldName] = mapped[f.fieldName] || formData[f.fieldName] || "";
        return acc;
      }, {});
      Object.assign(trimmed, addressValues);
      setFormData((prev) => ({ ...prev, ...addressValues }));
    }

    setSubmitting(true);
    setPollingMsg("");

    try {
      const dynamicValues = [...accountFields, ...addressFields].reduce(
        (acc, f) => {
          acc[f.fieldName] = formData[f.fieldName] ?? "";
          return acc;
        },
        {},
      );

      const payload = {
        userId,
        ...dynamicValues,
        entity_type:
          formData.entity_type === "INDIVIDUAL" ? "PERSONAL" : "COMPANY",
        first_name: formData.first_name,
        last_name: formData.last_name,
        country_code: formData.country_code,
        country_name: formData.country_name,
        account_currency: formData.account_currency,
        bank_country_code: formData.bank_country_code,
        transfer_method: selectedMethod,
        account_name: formData.first_name,
        account_routing_type1: formData.account_routing_type1 ?? "",
        account_routing_value1: formData.account_routing_value1 ?? "",
        account_routing_type2: formData.account_routing_type2 ?? "",
        account_routing_value2: formData.account_routing_value2 ?? "",
      };

      const response = await AddNewBeneficiary(payload);
      const resData = response?.data;

      if (
        (response?.status === 200 || resData?.status === 200) &&
        resData?.data
      ) {
        const newBeneficiaryId = resData.data.beneficiaryId;
        setPollingMsg("Verifying new beneficiary, please wait…");

        const resolved = await pollForStatus(userId, newBeneficiaryId);
        setPollingMsg("");

        if (!resolved) {
          setErrors({
            submit: [
              "Beneficiary verification is taking longer than expected. Returning to method selection...",
            ],
          });

          setSubmitting(false);
          setPollingMsg("");
          setTimeout(() => {
            setSelectedMethod("");
            setStep(STEP_MAIN);
            setErrors({});
          }, 4000);

          return;
        }

        if (resolved.status === "REJECTED") {
          setErrors({
            submit: [
              resolved?.errorMsg ||
                "Beneficiary was rejected. Please check the details and try again.",
            ],
          });
          setSubmitting(false);
          return;
        }

        onMethodChanged(resolved, selectedMethod);
      } else {
        setErrors({
          submit: [resData?.message || "Failed to create beneficiary."],
        });
      }
    } catch (err) {
      setErrors({
        submit: [err?.response?.data?.message || "Something went wrong."],
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingMethods) {
    return (
      <div className={styles.centered}>
        <Spinner animation="border" size="sm" />
      </div>
    );
  }

  if (!selectedMethod) {
    return (
      <div className={styles.changeMethodPanel} style={{ position: "relative" }}>
        {(submitting || pollingMsg) && (
          <div className={styles.fullScreenOverlay}>
            <Spinner animation="border" />
          </div>
        )}
        <p className={styles.changePanelTitle}>Choose a Transfer Method</p>
        {Object.entries(availableMethods).map(([key, meta]) => (
          <MethodCard
            key={key}
            methodKey={key}
            meta={meta}
            isSelected={false}
            onSelect={handleSelectMethod}
            showDetails={true}
          />
        ))}
        <div style={{ marginTop: 16 }}>
          <Button onClick={onCancel} title="Cancel" />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.changeMethodPanel} style={{ position: "relative" }}>
      <div ref={formTopRef} />

      {(submitting || pollingMsg) && (
        <div className={styles.fullScreenOverlay}>
          <Spinner animation="border" />
        </div>
      )}

      <p className={styles.changePanelTitle}>
        Create Beneficiary for{" "}
        <strong>
          {METHOD_DISPLAY[selectedMethod]?.title || selectedMethod}
        </strong>
      </p>

      {step === STEP_ADDRESS && (
        <button className={styles.backBtn} onClick={() => setStep(STEP_MAIN)}>
          ← Back
        </button>
      )}

      <Row>
        {accountFields.map((field) => (
          <Col md={12} key={field.fieldName} style={{ marginBottom: 12 }}>
            <InputField
              label={field.displayName}
              name={field.fieldName}
              placeholder={`Enter ${field.displayName}`}
              value={formData[field.fieldName] || ""}
              onChange={(e) => handleChange(field.fieldName, e.target.value)}
              error={!!errors[field.fieldName]}
              helperText={errors[field.fieldName]}
              required={field.required}
            />
          </Col>
        ))}
      </Row>

      {errors.submit && (
        <div className={styles.errorBanner}>
          {errors.submit.map((e, i) => (
            <div key={i}>{e}</div>
          ))}
        </div>
      )}

      <div className={styles.footerRow}>
        <Button
          onClick={handleSubmit}
          title="Create & Continue"
          disabled={submitting}
        />
        <Button onClick={onCancel} title="Cancel" disabled={submitting} />
      </div>
    </div>
  );
};

const CurrencyDropdownSwift = ({
  allCurrencies = [],
  selectedFromCurrency,
  value,
  onChange,
  disabled,
  className = "",
}) => {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  const selected = allCurrencies.find((opt) => opt.vendorCurrency === value);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div
      ref={dropdownRef}
      className={`${styles.currencyDropdown} ${className}`}
    >
      <div
        className={styles.currencySelected}
        onClick={() => !disabled && setOpen((prev) => !prev)}
      >
        <CurrencyFlag code={selected?.vendorCurrency} size={18} />
        <span style={{ marginLeft: 8 }}>{selected?.vendorCurrency}</span>
        <span className={styles.dropdownArrow}>▾</span>
      </div>

      {open && (
        <div className={styles.currencyMenu}>
          {allCurrencies.map((opt) => (
            <div
              key={opt.vendorCurrency}
              className={`${styles.currencyOption} ${
                opt.vendorCurrency === value
                  ? styles.currencyOptionActive
                  : ""
              }`}
              onClick={() => {
                onChange(opt.vendorCurrency);
                setOpen(false);
              }}
            >
              <CurrencyFlag code={opt.vendorCurrency} size={18} />
              <span style={{ marginLeft: 8 }}>{opt.vendorCurrency}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const TransferDetailsStep = ({
  beneficiary,
  userId,
  allBeneficiaries,
  onNext,
  onClose,
  initialData,
}) => {
  const [walletAccounts, setWalletAccounts] = useState([]);
  const [walletLoading, setWalletLoading] = useState(true);
  const [fromFocused, setFromFocused] = useState(false);
  const [toFocused, setToFocused] = useState(false);

  const [fromCurrency, setFromCurrency] = useState(null);
  const [toCurrency, setToCurrency] = useState(null);
  const [fromAmount, setFromAmount] = useState("");
  const [toAmount, setToAmount] = useState("");
  const [fromAmountError, setFromAmountError] = useState("");

  const [charges, setCharges] = useState({ totalFees: "-", rate: 0 });
  const [feeLoading, setFeeLoading] = useState(false);

  const [activeInput, setActiveInput] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState(
    beneficiary?.method || beneficiary?.transfer_method || "LOCAL",
  );
  const [activeBeneficiary, setActiveBeneficiary] = useState(beneficiary);
  const [showChangeMethod, setShowChangeMethod] = useState(false);
  const isInitializingRef = useRef(false);

  useEffect(() => {
    const fetch = async () => {
      setWalletLoading(true);
      try {
        const res = await WalletTransfer(userId, "");
        const accounts = res?.data?.data?.accounts || res?.data?.accounts || [];
        setWalletAccounts(accounts);
        const first =
          accounts.find((a) => parseFloat(a.balance) > 0) || accounts[0];
        setFromCurrency(first);
      } catch (err) {
        console.error("Error fetching wallet:", err);
      } finally {
        setWalletLoading(false);
      }
    };
    fetch();
  }, [userId]);

  useEffect(() => {
    if (initialData) {
      isInitializingRef.current = true;
      setFromAmount(String(initialData.fromAmount || ""));
      setToAmount(String(initialData.toAmount || ""));
      setFromCurrency(initialData.fromCurrency);
      setToCurrency(initialData.toCurrency);
      setCharges(initialData.charges);
      setSelectedMethod(initialData.selectedMethod);
      setActiveBeneficiary(initialData.activeBeneficiary);
      setTimeout(() => {
        isInitializingRef.current = false;
      }, 0);
    }
  }, [initialData]);

  useEffect(() => {
    if (activeBeneficiary?.currency || activeBeneficiary?.account_currency) {
      const beniCurr =
        activeBeneficiary.currency || activeBeneficiary.account_currency;
      setToCurrency({ vendorCurrency: beniCurr });
    }
  }, [activeBeneficiary]);

  useEffect(() => {
    const fetchRate = async () => {
      if (!fromCurrency?.vendorCurrency || !toCurrency?.vendorCurrency) return;
      if (isInitializingRef.current) return;
      try {
        const res = await TransferFee(
          undefined,
          1,
          toCurrency.vendorCurrency,
          fromCurrency.vendorCurrency,
          selectedMethod,
          "TRANSFER",
          activeBeneficiary?.country || activeBeneficiary?.country_code || "",
          userId,
        );
        setCharges({
          totalFees: "-",
          rate: res?.data?.data?.rate || res?.data?.rate || 0,
        });
      } catch (err) {
        console.error("Rate fetch error:", err);
      }
    };
    fetchRate();
  }, [
    fromCurrency?.vendorCurrency,
    toCurrency?.vendorCurrency,
    selectedMethod,
    userId,
  ]);

  const CurrencyDropdown = ({
    options = [],
    value,
    onChange,
    disabled,
    className = "",
  }) => {
    const [open, setOpen] = useState(false);
    const dropdownRef = useRef(null);

    const selected = options.find((opt) => opt.vendorCurrency === value);

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target)
        ) {
          setOpen(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, []);

    return (
      <div
        ref={dropdownRef}
        className={`${styles.currencyDropdown} ${className}`}
      >
        <div
          className={styles.currencySelected}
          onClick={() => !disabled && setOpen((prev) => !prev)}
        >
          <CurrencyFlag code={selected?.vendorCurrency} size={18} />
          <span style={{ marginLeft: 8 }}>{selected?.vendorCurrency}</span>
          <span className={styles.dropdownArrow}>▾</span>
        </div>

        {open && (
          <div className={styles.currencyMenu}>
            {options.map((opt) => (
              <div
                key={opt.vendorCurrency}
                className={`${styles.currencyOption} ${
                  opt.vendorCurrency === value
                    ? styles.currencyOptionActive
                    : ""
                }  ${
                  parseFloat(opt.balance) === 0 ||
                  parseFloat(opt.balance - (opt.holdBalance || 0)) === 0
                    ? styles.currencyOptionDisabled
                    : ""
                }`}
                onClick={() => {
                const available = parseFloat(opt.balance - (opt.holdBalance || 0)) || 0;
                  if (parseFloat(opt.balance) === 0 || available === 0) return;
                  onChange(opt.vendorCurrency);
                  setOpen(false);
                }}
              >
                <CurrencyFlag code={opt.vendorCurrency} size={18} />
                <span style={{ marginLeft: 8 }}>{opt.vendorCurrency}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const fetchConversion = useCallback(
    async (payload) => {
      if (!payload.fromCurrency || !payload.toCurrency) return;
      setFeeLoading(true);
      try {
        const res = await TransferFee(
          payload.buyAmount,
          payload.sellAmount,
          payload.toCurrency,
          payload.fromCurrency,
          payload.method,
          "TRANSFER",
          payload.country,
          userId,
        );
        const data = res?.data?.data || res?.data || {};
        setCharges({ totalFees: data.totalFees ?? "-", rate: data.rate ?? 0 });

      if (payload.__silent !== true) {
        if (payload.sellAmount !== undefined) {
          setFromAmount(String(payload.sellAmount));
          setToAmount(data.toAmount != null ? String(data.toAmount) : "");
        } else {
          setToAmount(
            data.toAmount != null
              ? String(data.toAmount)
              : String(payload.buyAmount),
          );
          setFromAmount(data.fromAmount != null ? String(data.fromAmount) : "");
        }
        }

        const computedFrom =
          payload.sellAmount !== undefined
            ? payload.sellAmount
            : data.fromAmount;
        const avail = parseFloat(
          fromCurrency?.balance - (fromCurrency?.holdBalance || 0),
        ).toFixed(2);
        validateFromAmount(computedFrom, avail, data.totalFees);
      } catch (err) {
        console.error("TransferFee error:", err);
      } finally {
        setFeeLoading(false);
        setActiveInput(null);
      }
    },
    [fromCurrency, userId],
  );

  const debouncedFetch = useDebounce(fetchConversion, 1200);

  useEffect(() => {
    if (!fromCurrency?.vendorCurrency || !toCurrency?.vendorCurrency) return;
    if (activeInput === "FROM" && fromAmount) {
      debouncedFetch({
        fromCurrency: fromCurrency.vendorCurrency,
        toCurrency: toCurrency.vendorCurrency,
        method: selectedMethod,
        country:
          activeBeneficiary?.country ||
          activeBeneficiary?.country_code ||
          "",
        sellAmount: fromAmount,
      });
    }

  if (activeInput === "TO" && toAmount) {
    debouncedFetch({
      fromCurrency: fromCurrency.vendorCurrency,
      toCurrency: toCurrency.vendorCurrency,
      method: selectedMethod,
      country:
        activeBeneficiary?.country || activeBeneficiary?.country_code || "",
       buyAmount: toAmount,
 });
    }
  }, [
    fromCurrency?.vendorCurrency,
    toCurrency?.vendorCurrency,
    selectedMethod,
    fromAmount,
    toAmount,
    activeInput,
  ]);

  const availableBalance = fromCurrency
    ? parseFloat(
        fromCurrency.balance - (fromCurrency.holdBalance || 0),
      ).toFixed(2)
    : "0.00";

  const validateFromAmount = (val, balance, fee) => {
    const parsed = parseFloat(val) || 0;
    if (!val || parsed <= 0) {
      setFromAmountError("Please enter a valid amount.");
      return false;
    }
    if (parsed > parseFloat(balance)) {
      setFromAmountError(
        `Amount cannot be greater than available balance (${balance} ${fromCurrency?.vendorCurrency}).`,
      );
      return false;
    }
    const total = parsed + (parseFloat(fee) || 0);
    if (total > parseFloat(balance)) {
      setFromAmountError(
        `Sum of transfer amount and transfer fee cannot be greater than available balance (${balance} ${fromCurrency?.vendorCurrency}).`,
      );
      return false;
    }
    setFromAmountError("");
    return true;
  };

  const handleFromAmountChange = (e) => {
    const val = e.target.value;
    setActiveInput("FROM");
    if (!val) {
      setFromAmount("");
      setToAmount("");
      setFromAmountError("");
      return;
    }
    setFromAmount(val);
    validateFromAmount(val, availableBalance, charges.totalFees);
  };

  const handleToAmountChange = (e) => {
    const val = e.target.value;
    setActiveInput("TO");
    if (!val) {
      setFromAmount("");
      setToAmount("");
      return;
    }
    setToAmount(val);
  };

  const handleFromCurrencyChange = (val) => {
    const acc = walletAccounts.find((a) => a.vendorCurrency === val);
    setFromCurrency(acc);
    setFromAmountError("");
    setActiveInput("FROM");
  };

  const handleMethodChanged = (newBeneficiary, newMethod) => {
    setActiveBeneficiary(newBeneficiary);
    setSelectedMethod(newMethod);
    const beniCurr = newBeneficiary.currency || newBeneficiary.account_currency;
    if (beniCurr) setToCurrency({ vendorCurrency: beniCurr });
    setShowChangeMethod(false);
    setActiveInput("FROM");
  };

  const getFeeDisplay = () => {
    if (!charges.totalFees || charges.totalFees === "-") return "-";
    const isZero = parseFloat(charges.totalFees) === 0;
    return isZero
      ? "Free"
      : `${charges.totalFees} ${fromCurrency?.vendorCurrency || ""}`;
  };

  const handleNext = () => {
    if (!fromAmount || !toAmount) {
      setFromAmountError("Please enter an amount.");
      return;
    }
    if (fromAmountError) return;
    onNext({
      fromAmount,
      toAmount,
      fromCurrency,
      toCurrency,
      charges,
      selectedMethod,
      activeBeneficiary,
    });
  };

  const isSameCurrency =
    fromCurrency?.vendorCurrency === toCurrency?.vendorCurrency;

  if (walletLoading) {
    return (
      <div className={styles.centered}>
        <Spinner animation="border" size="sm" />
      </div>
    );
  }

  if (showChangeMethod) {
    return (
      <ChangeMethodPanel
        beneficiary={activeBeneficiary}
        userId={userId}
        existingBeneficiaries={allBeneficiaries}
        onMethodChanged={handleMethodChanged}
        onCancel={() => setShowChangeMethod(false)}
      />
    );
  }

  return (
    <div className={styles.stepContainer}>
      {feeLoading && (
        <div className={styles.fullScreenOverlay}>
          <Spinner animation="border" />
        </div>
      )}
      <BeneficiaryCard beneficiary={activeBeneficiary} />

      {/* ── You Pay ── */}
      <div className={styles.amountSection}>
        <p className={styles.amountLabel}>You Pay</p>
        <div
          className={`${styles.amountRow} ${
            fromFocused ? styles.amountRowFocused : ""
          } ${fromAmountError ? styles.amountRowError : ""}`}
        >
          <input
            type="number"
            className={styles.amountInput}
            placeholder=""
            value={fromAmount}
            onChange={handleFromAmountChange}
            disabled={feeLoading}
            onWheel={(e) => e.target.blur()}
            onFocus={() => setFromFocused(true)}
            onBlur={() => setFromFocused(false)}
          />
          <CurrencyDropdown
            options={walletAccounts}
            value={fromCurrency?.vendorCurrency}
            onChange={handleFromCurrencyChange}
            disabled={feeLoading}
            className={styles.currencyInsideInput}
          />
        </div>
        {fromAmountError && (
          <p className={styles.errorText}>{fromAmountError}</p>
        )}
        <p className={styles.availableText}>
          Available Amount: {availableBalance} {fromCurrency?.vendorCurrency}
        </p>
      </div>

      <div className={styles.conversionBlock}>
        <div className={styles.conversionLine} />
        <div className={styles.conversionRow}>
          <span className={styles.convIcon}>
            <div className="transform rotate-25 text-greyish">
              <PaperAirplaneIcon />
            </div>
          </span>
          <div className={styles.convContent}>
            <p className={styles.convLabel}>Transfer Method</p>
            <MethodCard
              methodKey={selectedMethod}
              meta={
                METHOD_DISPLAY[selectedMethod] || {
                  title: selectedMethod,
                  duration: "",
                }
              }
              isSelected={true}
              onSelect={() => setShowChangeMethod(true)}
              showDetails={false}
            />
          </div>
        </div>
        {!isSameCurrency && (
          <div className={styles.conversionRow}>
            <span className={styles.convIcon}>
              <img src={Refresh} alt="rate" width={18} height={18} />
            </span>
            <div className={styles.convContent}>
              <p className={styles.convLabel}>
                Conversion Rate
                <span className={styles.convValue}>
                  {` 1 ${fromCurrency?.vendorCurrency} = ${charges?.rate ?? "-"} ${toCurrency?.vendorCurrency}`}
                </span>
                <InfoTooltip text="Actual rate may vary at time of transfer." />
              </p>
            </div>
          </div>
        )}
        <div className={styles.conversionRow}>
          <span className={styles.convIcon}>
            <img src={Dollar} alt="fee" width={18} height={18} />
          </span>
          <div className={styles.convContent}>
            <p className={styles.convLabel}>
              Transfer Fee:
              <span className={styles.convValue}>{` ${getFeeDisplay()}`}</span>
              <span className={styles.feeTooltipWrapper}>
              <InfoTooltip text="Actual fee may vary depending on the rate." />
              </span>
            </p>
          </div>
        </div>
      </div>

      <div className={styles.amountSection}>
        <p className={styles.amountLabel}>Payee Gets</p>
        <div
          className={`${styles.amountRow} ${toFocused ? styles.amountRowFocused : ""}`}
        >
          <input
            type="number"
            className={styles.amountInput}
            placeholder=""
            value={toAmount}
            onChange={handleToAmountChange}
            disabled={feeLoading}
            onWheel={(e) => e.target.blur()}
            onFocus={() => setToFocused(true)}
            onBlur={() => setToFocused(false)}
          />
          {selectedMethod === "LOCAL" ? (
            <div className={styles.currencyLocked}>
              <CurrencyFlag code={toCurrency?.vendorCurrency} size={20} />
              <span style={{ marginLeft: 6 }}>
                {toCurrency?.vendorCurrency}
              </span>
            </div>
          ) : (
            <CurrencyDropdownSwift
              allCurrencies={walletAccounts}
              selectedFromCurrency={fromCurrency?.vendorCurrency}
              value={toCurrency?.vendorCurrency}
              onChange={(val) => {
                setToCurrency({ vendorCurrency: val });
                setActiveInput("FROM");
              }}
              disabled={feeLoading}
            />
          )}
        </div>
      </div>

      <div className={styles.footerRow}>
        <Button onClick={onClose} title="Cancel" />
        <Button
          onClick={handleNext}
          title="Continue"
          disabled={feeLoading || !!fromAmountError}
          loading={feeLoading}
        />
      </div>
    </div>
  );
};

const PaymentSummaryStep = ({
  transferData,
  onBack,
  onConfirm,
  submitting,
}) => {
  const [purpose, setPurpose] = useState("");
  const [remarks, setRemarks] = useState("");
  const [purposeError, setPurposeError] = useState("");
  const [remarksError, setRemarksError] = useState("");

  const {
    fromAmount,
    toAmount,
    fromCurrency,
    toCurrency,
    charges,
    selectedMethod,
    activeBeneficiary,
  } = transferData;

  const handleConfirm = () => {
    let valid = true;
    if (!purpose) {
      setPurposeError("Purpose of transfer is required.");
      valid = false;
    }
    if (!remarks) {
      setRemarksError("Remarks are required.");
      valid = false;
    }
    if (!valid) return;
    onConfirm({ purpose, remarks });
  };

  return (
    <div className={styles.stepContainer}>
      <div className={styles.backpayment}>
        <button
            type="button"
            className={styles.backButton}
            onClick={onBack}
            aria-label="Go back"
          >
            <img src={singleBackArrow} alt="Back" className={styles.backIcon} />
          </button>
      <p className={styles.stepTitle}>Payment Summary</p>
      </div>
      <div className={styles.summaryCard}>
        <BeneficiaryCard beneficiary={activeBeneficiary} />

        <div className={styles.summaryRows}>
          <div className={styles.summaryRow}>
            <span className={styles.summaryKey}>You Pay</span>
            <span className={styles.summaryVal}>
              <span style={{ marginRight: 6 }}>
                {fromAmount} {fromCurrency?.vendorCurrency}
              </span>
              <CurrencyFlag code={fromCurrency?.vendorCurrency} size={16} />
            </span>
          </div>
          <div className={styles.summaryRow}>
            <span className={styles.summaryKey}>Payee Gets</span>
            <span className={styles.summaryVal}>
              <span style={{ marginRight: 6 }}>
                {toAmount} {toCurrency?.vendorCurrency}
              </span>
              <CurrencyFlag code={toCurrency?.vendorCurrency} size={16} />
            </span>
          </div>
          <div className={styles.summaryRow}>
            <span className={styles.summaryKey}>Account balance</span>
            <span className={styles.summaryVal}>
              <span style={{ marginRight: 6 }}>
                 {fromCurrency?.balance - fromCurrency?.holdBalance}
              </span>
            </span>
          </div>
          <div className={styles.summaryRow}>
            <span className={styles.summaryKey}>Transfer On</span>
            <span className={styles.summaryVal}>
              <div style={{ display: "flex", flexDirection: "column", textAlign:"end" }}>
                <span>
                  {reFormatDate(new Date())}
                </span>
                <span className={styles.summaryDuration}>
                  Takes {METHOD_DISPLAY[selectedMethod]?.duration || ""} to arrive
                </span>
              </div>
            </span>
          </div>
          {charges.rate &&
            !isNaN(charges.rate) &&
            fromCurrency?.vendorCurrency !== toCurrency?.vendorCurrency && (
              <div className={styles.summaryRow}>
                <span className={styles.summaryKey}>
                  Conversion Rate
                </span>
                <span className={styles.summaryVal}>
                1 {fromCurrency?.vendorCurrency} = {charges?.rate}{" "}
                {toCurrency?.vendorCurrency}
                <span className={styles.tooltipSpace}>
                    <InfoTooltip text="Actual rate may vary at time of transfer." />
                </span>
                </span>
              </div>
            )}
          <div className={styles.summaryRow}>
            <span className={styles.summaryKey}>
              Transfer Fee
            </span>
            <span
              className={`${styles.summaryVal} ${parseFloat(charges.totalFees) === 0 ? styles.freeText : ""}`}
            >
              {charges.totalFees === "-" || !charges.totalFees
                ? "-"
                : parseFloat(charges.totalFees) === 0
                  ? "Free"
                  : `${charges.totalFees} ${fromCurrency?.vendorCurrency}`}
                <span className={styles.tooltipSpace}>
                    <InfoTooltip text="Actual rate may vary at time of transfer." />
                </span>
            </span>
          </div>
          <div className={styles.summaryRow}>
            <span className={styles.summaryKey}>
              Method
            </span>
            <span className={styles.summaryVal} style={{textAlign:"end"}}>
  {METHOD_DISPLAY[selectedMethod]?.title || selectedMethod}
  <br />
  <span className={styles.summaryDuration}>
    ({METHOD_DISPLAY[selectedMethod]?.duration || ""})
  </span>
</span>
          </div>
        </div>
      </div>

      <div className={styles.formSection}>
        <DropDown
          label="Purpose of Transfer"
          name="purpose"
          required
          placeholder="Select Purpose"
          data={PURPOSE_OPTIONS}
          value={purpose}
          onChange={(val) => {
            setPurpose(val);
            setPurposeError("");
          }}
          error={!!purposeError}
          helperText={purposeError}
          isSearchable={false}
        />
        <div style={{ marginTop: 16 }}>
          <InputField
            label="Remarks"
            name="remarks"
            placeholder="Remarks"
            value={remarks}
            onChange={(e) => {
              setRemarks(e.target.value);
              setRemarksError("");
            }}
            error={!!remarksError}
            helperText={remarksError}
            required
          />
        </div>
      </div>

      <div className={styles.footerRow}>
        <Button onClick={onBack} title="Cancel" disabled={submitting} />
        <Button
          onClick={handleConfirm}
          title="Confirm Transfer"
          disabled={submitting}
        />
      </div>
    </div>
  );
};

const ConfirmationStep = ({ transferResult, onClose }) => {
  const isSuccess = transferResult?.success;

  if (!isSuccess) {
    return (
      <div className={styles.confirmationContainer}>
        <p className={styles.confirmationTitle}>Transfer Failed</p>
        <p className={styles.confirmationSub}>
          {transferResult?.message ||
            "Something went wrong. Please try again."}
        </p>
        <div style={{ marginTop: 24 }}>
          <Button onClick={onClose} title="Back to Home" />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.confirmationContainer}>
      <div className={styles.confirmationIcon}>
        <img
          src={SuccessVideo}
          alt="success"
          width={70}
          height={70}
          style={{ borderRadius: "50%" }}
        />
      </div>

      <p className={styles.confirmationTitle}>
        Your payment has been initiated
      </p>

      <p className={styles.confirmationSub}>
        Transfer ID : {transferResult.transactionNo}
      </p>

      <div className={styles.summaryCard} style={{ marginTop: 20 }}>

        <div className={styles.summaryRow}>
          <span className={styles.summaryKey}>You Paid</span>
          <span className={styles.summaryVal}>
            {transferResult.amount} {transferResult.fromCurrency}
          </span>
        </div>

        <div className={styles.summaryRow}>
          <span className={styles.summaryKey}>Exchange Rate</span>
          <span className={styles.summaryVal}>
            1 {transferResult.fromCurrency} ={" "}
            {transferResult.forexRate} {transferResult.toCurrency}
          </span>
        </div>

        <div className={styles.summaryRow}>
          <span className={styles.summaryKey}>Transfer Fee</span>
          <span className={styles.summaryVal}>
            {transferResult.fees} {transferResult.fromCurrency}
          </span>
        </div>

        <div className={styles.summaryRow}>
          <span className={styles.summaryKey}>Receiver Name</span>
          <span className={styles.summaryVal}>
            {transferResult.receiverName}
          </span>
        </div>

        <div className={styles.summaryRow}>
          <span className={styles.summaryKey}>Transfer Method</span>
          <span className={styles.summaryVal}>
            {transferResult.transferMethod}
          </span>
        </div>

        <div className={styles.summaryRow}>
          <span className={styles.summaryKey}>Account Number</span>
          <span className={styles.summaryVal}>
            {transferResult.accountNo}
          </span>
        </div>

        <div className={styles.summaryRow}>
          <span className={styles.summaryKey}>Transfer Date</span>
          <span className={styles.summaryVal}>
            {reFormatDate(transferResult.createdAt)}
          </span>
        </div>
      </div>

      <div style={{ marginTop: 20 }}>
        <Button onClick={onClose} title="Back to Home" />
      </div>
    </div>
  );
};
const LienTransferFlow = ({ beneficiary, userId, onClose }) => {
  const [step, setStep] = useState(TRANSFER_STEPS.DETAILS);
  const [transferData, setTransferData] = useState(null);
  const [transferResult, setTransferResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [allBeneficiaries, setAllBeneficiaries] = useState([]);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await LienBeneficiary(userId, 1, 100, "APPROVED");
        setAllBeneficiaries(res?.data?.data?.beneficiaries || []);
      } catch {}
    };
    fetch();
  }, [userId]);

  const handleDetailsNext = (data) => {
    setTransferData(data);
    setStep(TRANSFER_STEPS.SUMMARY);
  };

  const handleConfirmTransfer = async ({ purpose, remarks }) => {
    if (!transferData) return;
    setSubmitting(true);
    try {
      const {
        fromAmount,
        toAmount,
        fromCurrency,
        toCurrency,
        charges,
        selectedMethod,
        activeBeneficiary,
      } = transferData;

      const payload = {
        userId,
        beneficiaryId: activeBeneficiary?.beneficiaryId,
        fromCurrency: fromCurrency?.vendorCurrency,
        toCurrency: toCurrency?.vendorCurrency,
        amount: parseFloat(toAmount),
        transferMethod: selectedMethod,
        transactionType: "TRANSFER",
        purpose,
        remarks,
      };

      const res = await ExecuteTransfer(payload);
      const resData = res?.data;

      if (res?.status === 200 || resData?.status === 200) {
        const data = resData?.data || {};

        setTransferResult({
          success: true,
          transactionNo: data.transactionNo,
          debitAmount: data.debitAmount,
          fromCurrency: data.fromCurrency,
          amount: data.amount,
          toCurrency: data.toCurrency,
          forexRate: data.forexRate,
          fees: data.fees,
          receiverName: data.receiverName,
          transferMethod: data.transferMethod,
          accountNo: data.accountNo,
          createdAt: data.createdAt,
        });
      } else {
        setTransferResult({
          success: false,
          message: resData?.message || "Transfer failed. Please try again.",
        });
      }
      setStep(TRANSFER_STEPS.CONFIRMATION);
    } catch (err) {
      setTransferResult({
        success: false,
        message:
          err?.response?.data?.message ||
          "Something went wrong. Please try again.",
      });
      setStep(TRANSFER_STEPS.CONFIRMATION);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.flowRoot}>
      {submitting && (
        <div className={styles.fullScreenOverlay}>
          <Spinner animation="border" />
        </div>
      )}
      <div className={styles.progressBar}>
        {["Transfer Details", "Summary", "Confirmation"].map((label, idx) => (
          <React.Fragment key={label}>
            <div className={styles.progressStep}>
              <div
                className={`${styles.progressDot} ${step > idx ? styles.progressDotDone : step === idx + 1 ? styles.progressDotActive : ""}`}
              >
                {step > idx + 1 ? (
                  <img src={greenTick} alt="done" width={16} height={16} style={{background:"white" ,borderRadius:"50%"}} />
                ) : (
                  idx + 1
                )}
              </div>
              <span
                className={`${styles.progressLabel} ${step === idx + 1 ? styles.progressLabelActive : ""}`}
              >
                {label}
              </span>
            </div>
            {idx < 2 && (
              <div
                className={`${styles.progressLine} ${step > idx + 1 ? styles.progressLineDone : ""}`}
              />
            )}
          </React.Fragment>
        ))}
      </div>

      {step === TRANSFER_STEPS.DETAILS && (
        <TransferDetailsStep
          beneficiary={beneficiary}
          userId={userId}
          allBeneficiaries={allBeneficiaries}
          onNext={handleDetailsNext}
          onClose={onClose}
          initialData={transferData}
        />
      )}
      {step === TRANSFER_STEPS.SUMMARY && (
        <PaymentSummaryStep
          transferData={transferData}
          onBack={() => setStep(TRANSFER_STEPS.DETAILS)}
          onConfirm={handleConfirmTransfer}
          submitting={submitting}
        />
      )}
      {step === TRANSFER_STEPS.CONFIRMATION && (
        <ConfirmationStep transferResult={transferResult} onClose={onClose} />
      )}
    </div>
  );
};

export default LienTransferFlow;
